import type { LocationQuery } from 'vue-router'

/**
 * V1 client portal (clientportal-core / HMI) links send `/login?redirectUrl=<absolute URL>`;
 * Compass auth only understands `?redirect=<internal path>`. The auth middleware rewrites the
 * param once so every auth screen keeps reading `redirect`. Legacy `/affiliate/*` targets are
 * canonicalised afterwards by `affiliate-path-redirect.global`.
 */

// Control chars are stripped by URL parsers, so `/\t/evil.com` would become `//evil.com`.
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/

const isSafeInternalPath = (value: string): boolean =>
  value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\') && !CONTROL_CHARS.test(value)

/** Same-origin internal path for a V1 `redirectUrl` (path or absolute URL), else null. */
function toInternalRedirect(redirectUrl: string, origin: string): string | null {
  let target: string | null = redirectUrl
  if (!redirectUrl.startsWith('/')) {
    try {
      const url = new URL(redirectUrl)
      target = url.origin === origin ? `${url.pathname}${url.search}${url.hash}` : null
    } catch {
      // Not an absolute URL — treat as unsafe and drop it.
      return null
    }
  }
  return target && isSafeInternalPath(target) ? target : null
}

/**
 * Query with V1 `redirectUrl` swapped for Compass `redirect`, or null when `/login` carries no
 * `redirectUrl`. An explicit `redirect` wins; cross-origin or unsafe values are dropped.
 */
export function normalizeLegacyLoginQuery(path: string, query: LocationQuery, origin: string): LocationQuery | null {
  if (path !== '/login' || !('redirectUrl' in query)) return null

  const { redirectUrl, ...nextQuery } = query
  const redirect = !nextQuery.redirect && typeof redirectUrl === 'string' ? toInternalRedirect(redirectUrl, origin) : null
  return redirect ? { ...nextQuery, redirect } : nextQuery
}
